<!-- Updated: 2026-02-13 -->
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AppHeader from '@/components/AppHeader.vue'
import Timeline from '@/components/Timeline.vue'
import AppFooter from '@/components/AppFooter.vue'
import SubscriptionModal from '@/components/SubscriptionModal.vue'
import DataPrivacyModal from '@/components/DataPrivacyModal.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import AdminPanel from '@/components/admin/AdminPanel.vue'
import { useI18n } from '@/composables/useI18n'
import { useEvents } from '@/composables/useEvents'
import { useAuth } from '@/composables/useAuth'
import { useSubscription } from '@/composables/useSubscription'
import type { Entry } from '@/types'

const { setLanguage, lang } = useI18n()
const { loadEvents, loadServices, loadComponents } = useEvents()
const { checkSession, isAuthenticated } = useAuth()
const { checkConfirmationToken, checkUnsubscribeToken, checkPreferencesToken, loadSubscriptionState } = useSubscription()

const adminPanel = ref<InstanceType<typeof AdminPanel> | null>(null)

function handleNewEntry() {
  adminPanel.value?.openNewEntry()
}

function handleEditEntry(entry: Entry) {
  adminPanel.value?.openEditEntry(entry)
}

function handlePublishEntry(entryId: string) {
  adminPanel.value?.openPublishModal(entryId)
}

function handleDeleteEntry(entryId: string) {
  adminPanel.value?.confirmAndDelete(entryId)
}

onMounted(async () => {
  // Check for confirmation / unsubscribe / preferences tokens in URL
  await checkConfirmationToken()
  await checkUnsubscribeToken()

  // Load catalogs and events
  await Promise.all([loadServices(), loadComponents()])

  // Check preferences token (needs services loaded for scope mapping)
  await checkPreferencesToken()

  // Load subscription state from cookie (if any)
  await loadSubscriptionState()

  // Check admin session
  await checkSession()

  // Load events (uses admin endpoint if authenticated)
  await loadEvents()
})
</script>

<template>
  <div id="app">
    <AppHeader @add-entry="handleNewEntry" />
    <Timeline
      @edit-entry="handleEditEntry"
      @publish-entry="handlePublishEntry"
      @delete-entry="handleDeleteEntry"
    />
    <AppFooter />
  </div>

  <SubscriptionModal />
  <DataPrivacyModal />
  <ToastContainer />
  <AdminPanel v-if="isAuthenticated" ref="adminPanel" />
</template>
